/**
 * The shape of the frontend's configuration.
 *
 * Deliberately free of any `import.meta.env` access so it can be imported from
 * two very different places: the browser bundle (src/config/env.ts) and the
 * Vite config running in Node at build time. One schema, checked twice — once
 * before the bundle is produced and once when it boots — with no chance of the
 * two definitions drifting apart.
 */
import { z } from 'zod';

export const envSchema = z.object({
  /** Base URL of kdoshsop-api. Empty in development, where Vite proxies /api. */
  VITE_API_URL: z.string().default(''),

  /**
   * Which deployed environment this bundle is for.
   *
   * 'local' is a developer machine; 'prod' is the deployed environment.
   * kdoshsop has no 'dev' deployment yet — the value is kept because the
   * infrastructure is ready for one and adding it should not be a schema
   * change on the day it appears.
   *
   * Spelled as the infrastructure spells it: kdoshr-infra passes
   * VITE_ENVIRONMENT="$ENV" straight from its deploy script. 'development' or
   * 'production' would fail the build here, which is the intent — this schema
   * is validated in vite.config.ts at build time as well as at boot.
   */
  VITE_ENVIRONMENT: z.enum(['local', 'dev', 'prod']).default('local'),

  /** Optional: only set once error reporting is provisioned. */
  VITE_SENTRY_DSN: z.string().optional(),

  /** Shown in the UI and attached to error reports, so a bug maps to a build. */
  VITE_APP_VERSION: z.string().default('dev'),

  VITE_ENABLE_DEVTOOLS: z
    .string()
    .default('false')
    .transform((value) => value === 'true'),
});

export type Env = z.infer<typeof envSchema>;

/** Formats issues identically wherever validation runs. */
export function formatEnvIssues(error: z.ZodError): string {
  return error.issues.map((issue) => `  · ${issue.path.join('.')}: ${issue.message}`).join('\n');
}
